import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
  TAG_COVERAGE_PRODUCTS,
  type TagCoverage as Coverage,
  type TagCoverageProduct,
} from "../../api/posthogReportsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";

const ALL = "all";
const WINDOWS = [7, 30, 90] as const;
const NO_AREA = "(no area)";

function percent(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—";
}

/** How much of the click trail carries an explicit tag, per product and area,
 *  and the untagged elements clicked most — the tagging to-do list (spec C7).
 *
 *  A failure here stays inside this card: coverage comes from a separate
 *  route, and the report above it is still useful without it. */
export function TagCoverage() {
  const { posthogReports } = useAuthedApi();
  const [product, setProduct] = useState<TagCoverageProduct | typeof ALL>(ALL);
  const [days, setDays] = useState<number>(30);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // A slow response for an old filter must not overwrite a newer one.
    let current = true;
    setLoading(true);
    setFailed(false);
    posthogReports
      .getTagCoverage(product === ALL ? { days } : { product, days })
      .then((result) => current && setCoverage(result))
      .catch((e: unknown) => {
        console.error("tag coverage fetch failed", e);
        if (current) {
          setFailed(true);
          setCoverage(null);
        }
      })
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [posthogReports, product, days]);

  const clicks = coverage?.summary.reduce((sum, row) => sum + row.clicks, 0) ?? 0;
  const explicit = coverage?.summary.reduce((sum, row) => sum + row.explicit, 0) ?? 0;

  return (
    <WorkspaceCard sx={{ mt: 2 }}>
      <Box data-ph-scope="tag_coverage">
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Tag coverage
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How many clicks carry an explicit tag, and which untagged elements to tag next.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl data-ph-scope="product" size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="tag-coverage-product-label">Product</InputLabel>
            <Select
              data-ph="open"
              labelId="tag-coverage-product-label"
              label="Product"
              value={product}
              onChange={(e) => setProduct(e.target.value as TagCoverageProduct | typeof ALL)}
              // The menu is portaled out of this card, so it names its whole path (D7).
              MenuProps={{ slotProps: { root: { "data-ph-scope": "organization-tag_coverage-product" } as object } }}
            >
              <MenuItem value={ALL} data-ph="select">All</MenuItem>
              {TAG_COVERAGE_PRODUCTS.map((id) => (
                <MenuItem key={id} value={id} data-ph="select">
                  {id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, next: number | null) => next && setDays(next)}
            aria-label="Window"
            data-ph-scope="days"
          >
            {WINDOWS.map((window) => (
              <ToggleButton key={window} value={window} data-ph="select">
                {window} days
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading tag coverage…
            </Typography>
          </Stack>
        ) : failed || !coverage ? (
          <Typography variant="body2" color="text.secondary">
            Tag coverage unavailable
          </Typography>
        ) : coverage.summary.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No clicks recorded in this window.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {percent(explicit, clicks)} of clicks explicitly tagged
            </Typography>
            <Table size="small" aria-label="Tag coverage by area" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell align="right">Explicit</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                  <TableCell align="right">Untagged</TableCell>
                  <TableCell align="right">Guessed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coverage.summary.map((row) => (
                  <TableRow key={`${row.product}/${row.area ?? ""}`}>
                    <TableCell>{row.product}</TableCell>
                    <TableCell>{row.area ?? NO_AREA}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {percent(row.explicit, row.clicks)}
                    </TableCell>
                    <TableCell align="right">{row.clicks}</TableCell>
                    <TableCell align="right">{row.untagged}</TableCell>
                    <TableCell align="right">{row.guessed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Next to tag
            </Typography>
            {coverage.next_to_tag.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Every clicked element has an explicit tag.
              </Typography>
            ) : (
              <Table size="small" aria-label="Next to tag">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Component</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell>Last seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coverage.next_to_tag.map((gap) => (
                    <TableRow
                      key={[gap.product, gap.area, gap.path, gap.component, gap.route, gap.tag_source].join("/")}
                    >
                      <TableCell>{gap.product}</TableCell>
                      <TableCell>{gap.area ?? NO_AREA}</TableCell>
                      <TableCell>{gap.path ?? "—"}</TableCell>
                      <TableCell>{gap.component ?? "—"}</TableCell>
                      <TableCell>{gap.route ?? "—"}</TableCell>
                      <TableCell>{gap.tag_source}</TableCell>
                      <TableCell align="right">{gap.clicks}</TableCell>
                      <TableCell>{new Date(gap.last_seen).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </Box>
    </WorkspaceCard>
  );
}
